// utils/signImageFilters.tsx - VERSÃO MELHORADA COM DIFERENCIAÇÃO VISUAL
import React from 'react';

export type ServiceId = 1 | 2 | 3 | 4;

export interface ServiceStyle {
  filter: string;
  overlay?: JSX.Element;
  description: string;
  borderColor?: string;
  opacity?: number;
}

/**
 * Retorna o estilo CSS (filtros) para a imagem baseado no service_id
 *
 * LÓGICA VISUAL:
 * - Service 1 (Implantação): Colorido brilhante (nascendo) 🟢
 * - Service 2 (Remoção): Cinza escuro + X vermelho (morrendo) 💀
 * - Service 3 (Limpeza): Colorido transparente + borda amarela (vivo mas sujo) 🧹
 * - Service 4 (Manter): Colorido transparente + borda azul (vivo e preservado) 💎
 */
export const getServiceImageStyle = (
  serviceId: ServiceId | number | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  isOverlay: boolean = false
): {
  filter: string;
  showXOverlay: boolean;
  borderColor?: string;
  opacity?: number;
  borderWidth?: string;
} => {
  // 🐛 DEBUG: Log no início
  console.log('🎯 [getServiceImageStyle] Chamada:', {
    serviceId,
    serviceIdType: typeof serviceId,
    isOverlay
  });

  // Se for overlay, aplicar filtro azul
  if (isOverlay) {
    console.log('→ Retornando filtro AZUL (overlay)');
    return {
      filter: 'sepia(100%) saturate(300%) brightness(70%) hue-rotate(180deg)',
      showXOverlay: false,
      opacity: 0.85
    };
  }

  // Aplicar filtros baseados no service_id
  switch (serviceId) {
    case 1:
      console.log('→ Service ID 1: COLORIDO BRILHANTE (implantação) 🟢');
      return {
        filter: 'brightness(110%) saturate(120%)', // Mais vivo e brilhante
        showXOverlay: false
      };

    case 2:
      console.log(
        '→ Service ID 2: CINZA ESCURO + X VERMELHO GRANDE (remoção) 💀'
      );
      return {
        filter: 'grayscale(100%) brightness(70%) contrast(90%)', // Escuro e "morto"
        showXOverlay: true
      };

    case 3:
      console.log(
        '→ Service ID 3: COLORIDO TRANSPARENTE + BORDA AMARELA (limpeza) 🧹'
      );
      return {
        filter: 'opacity(0.6) saturate(110%)', // Transparente mas colorido
        showXOverlay: false,
        borderColor: '#0dcaf0', // Azul claro/cyan
        borderWidth: '4px',
        opacity: 0.6
      };

    case 4:
      console.log(
        '→ Service ID 4: COLORIDO TRANSPARENTE + BORDA AZUL (manter) 💎'
      );
      return {
        filter: 'opacity(0.75) saturate(100%)', // Levemente transparente
        showXOverlay: false,
        borderColor: '#ffc107', // Amarelo/laranja
        borderWidth: '4px',
        opacity: 0.75
      };

    default:
      console.warn(
        '⚠️ Service ID INVÁLIDO ou undefined:',
        serviceId,
        '- Retornando colorido'
      );
      return {
        filter: 'none',
        showXOverlay: false
      };
  }
};

/**
 * Retorna o nome do serviço baseado no service_id
 */
export const getServiceName = (
  serviceId: ServiceId | number | null | undefined
): string => {
  switch (serviceId) {
    case 1:
      return 'Implantação';
    case 2:
      return 'Remoção';
    case 3:
      return 'Limpeza';
    case 4:
      return 'Manter';
    default:
      return 'N/A';
  }
};

/**
 * Componente React para renderizar o X sobre a imagem de remoção
 * VERSÃO MELHORADA: X vermelho mais visível
 */
export const XOverlay: React.FC<{
  size?: number;
  color?: string;
  strokeWidth?: number;
}> = ({ size = 40, color = '#dc3545', strokeWidth = 4 }) => {
  //console.log('❌ [XOverlay] Renderizado com size:', size);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))' // Sombra mais forte
      }}
    >
      {/* X vermelho mais grosso */}
      <line
        x1="8"
        y1="8"
        x2="32"
        y2="32"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1="32"
        y1="8"
        x2="8"
        y2="32"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
};
